import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '@app/components/Drawer';
import LabelPairsEditor from '@app/components/LabelPairsEditor';
import { SaveIcon } from '@app/components/icons';
import {
  CredentialApiAuthTypeschema,
  CredentialDetailsApiRestAuthApiKeyInEnum,
  CredentialDetailsApiRestAuthBasic,
  CredentialDetailsApiRestAuthBearer,
  CredentialDetailsApiRestAuthMtls,
  CredentialKindschema,
  SecretTypeschema,
  SnmpV3AuthProtocolschema,
  SnmpV3PrivProtocolschema,
  SnmpV3SecurityLevelschema
} from '@app/types/sdk';
import type {
  Credential,
  CredentialApiAuthType,
  CredentialCreate,
  CredentialDetailsApiRestAuth,
  CredentialDetailsApiRestAuthApiKey,
  CredentialDetailsCliSshschema,
  CredentialDetailsGnmischema,
  CredentialDetailsSnmpV2cschema,
  CredentialDetailsSnmpV3schema,
  CredentialStore,
  CredentialStoresListResponse,
  Secret,
  SecretCreate
} from '@app/types/sdk';
import { useCredentialStoresApi, useCredentialsApi, useSecretsApi } from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { resolveErrorMessage } from '@app/utils/errors';
import { labelPairsToRecord, mapLabelsToPairs, type LabelPair } from '@app/utils/labels';
import { containsCertificatePemBlock, deleteSecretWithAuth } from '@app/utils/secrets';
type CliAuthMethod = 'password' | 'sshKey' | 'cyberark';
type CliDetailsWithAuth = CredentialDetailsCliSshschema & { auth?: string; cyberarkStoreId?: string };
type MtlsMode = 'pem' | 'pkcs12';
type ApiAuthTypeValue = CredentialApiAuthType;
type CredentialKindValue = Credential['kind'];
type SnmpSecurityLevel = CredentialDetailsSnmpV3schema['securityLevel'];
type SnmpAuthProtocol = NonNullable<CredentialDetailsSnmpV3schema['authProtocol']>;
type SnmpPrivProtocol = NonNullable<CredentialDetailsSnmpV3schema['privProtocol']>;
type ApiKeyPlacement = CredentialDetailsApiRestAuthApiKey['_in'];

export interface CredentialWizardProps {
  orgId: string;
  open: boolean;
  mode: 'create' | 'duplicate';
  initialCredential?: Credential | null;
  onClose: () => void;
  onCompleted: (credential: Credential, etag: string | null) => void;
}

interface CreatedSecret {
  secret: Secret;
  etag: string | null;
}

interface CliFormState {
  username: string;
  port: number;
  authMethod: CliAuthMethod;
  execPassword: string;
  enablePassword: string;
  includeEnablePassword: boolean;
  sshPrivateKey: string;
  sshPassphrase: string;
  cyberarkStoreId: string;
}

interface GnmiFormState {
  username: string;
  password: string;
  port: number;
  deviceServerTrustCaBundle: string;
}

interface SnmpV2cFormState {
  port: number;
  readCommunity: string;
  writeCommunity: string;
  includeWriteCommunity: boolean;
}

interface SnmpV3FormState {
  username: string;
  port: number;
  engineId: string;
  contextName: string;
  securityLevel: SnmpSecurityLevel;
  authProtocol: SnmpAuthProtocol | '';
  authPassword: string;
  privProtocol: SnmpPrivProtocol | '';
  privPassword: string;
}

interface ApiRestFormState {
  authType: ApiAuthTypeValue;
  apiKeyPlacement: ApiKeyPlacement;
  apiKeyName: string;
  apiKeyValue: string;
  basicUsername: string;
  basicPassword: string;
  bearerToken: string;
  mtlsMode: MtlsMode;
  mtlsClientCert: string;
  mtlsClientKey: string;
  mtlsCaBundle: string;
  mtlsPkcs12: string;
  mtlsPkcs12Passphrase: string;
}

const LABELS_EMPTY_HINT = 'Labels help target credentials to device subsets.';

const emptyCliState = (): CliFormState => ({
  username: '',
  port: 22,
  authMethod: 'password',
  execPassword: '',
  enablePassword: '',
  includeEnablePassword: false,
  sshPrivateKey: '',
  sshPassphrase: '',
  cyberarkStoreId: ''
});

const emptyGnmiState = (): GnmiFormState => ({
  username: '',
  password: '',
  port: 6030,
  deviceServerTrustCaBundle: ''
});

const emptySnmpV2cState = (): SnmpV2cFormState => ({
  port: 161,
  readCommunity: '',
  writeCommunity: '',
  includeWriteCommunity: false
});

const emptySnmpV3State = (): SnmpV3FormState => ({
  username: '',
  port: 161,
  engineId: '',
  contextName: '',
  securityLevel: SnmpV3SecurityLevelschema.NoAuthNoPriv,
  authProtocol: '',
  authPassword: '',
  privProtocol: '',
  privPassword: ''
});

const emptyApiRestState = (): ApiRestFormState => ({
  authType: CredentialApiAuthTypeschema.ApiKey,
  apiKeyPlacement: CredentialDetailsApiRestAuthApiKeyInEnum.Header,
  apiKeyName: 'X-Api-Key',
  apiKeyValue: '',
  basicUsername: '',
  basicPassword: '',
  bearerToken: '',
  mtlsMode: 'pem',
  mtlsClientCert: '',
  mtlsClientKey: '',
  mtlsCaBundle: '',
  mtlsPkcs12: '',
  mtlsPkcs12Passphrase: ''
});

const kindOptions: Array<{ value: CredentialKindValue; label: string; summary: string }> = [
  { value: CredentialKindschema.CliSsh, label: 'CLI (SSH)', summary: 'Username/password or SSH key for device CLIs.' },
  { value: CredentialKindschema.Gnmi, label: 'gNMI', summary: 'Username/password with strict server-authenticated TLS.' },
  { value: CredentialKindschema.SnmpV2c, label: 'SNMP v2c', summary: 'Read/write communities for SNMP agents.' },
  { value: CredentialKindschema.SnmpV3, label: 'SNMP v3', summary: 'User-based security with auth/priv secrets.' },
  { value: CredentialKindschema.ApiRest, label: 'API (REST)', summary: 'Bearer/API key/basic auth or mTLS materials.' }
];

const securityLevels: Array<{ value: SnmpSecurityLevel; label: string }> = [
  { value: SnmpV3SecurityLevelschema.NoAuthNoPriv, label: 'noAuthNoPriv' },
  { value: SnmpV3SecurityLevelschema.AuthNoPriv, label: 'authNoPriv' },
  { value: SnmpV3SecurityLevelschema.AuthPriv, label: 'authPriv' }
];

const authProtocols: Array<{ value: SnmpAuthProtocol; label: string }> = [
  { value: SnmpV3AuthProtocolschema.Md5, label: 'MD5' },
  { value: SnmpV3AuthProtocolschema.Sha, label: 'SHA' },
  { value: SnmpV3AuthProtocolschema.Sha224, label: 'SHA-224' },
  { value: SnmpV3AuthProtocolschema.Sha256, label: 'SHA-256' },
  { value: SnmpV3AuthProtocolschema.Sha384, label: 'SHA-384' },
  { value: SnmpV3AuthProtocolschema.Sha512, label: 'SHA-512' }
];

const privProtocols: Array<{ value: SnmpPrivProtocol; label: string }> = [
  { value: SnmpV3PrivProtocolschema.Des, label: 'DES' },
  { value: SnmpV3PrivProtocolschema.Aes128, label: 'AES-128' },
  { value: SnmpV3PrivProtocolschema.Aes192, label: 'AES-192' },
  { value: SnmpV3PrivProtocolschema.Aes256, label: 'AES-256' }
];

const apiAuthOptions: Array<{ value: ApiAuthTypeValue; label: string; description: string }> = [
  { value: CredentialApiAuthTypeschema.ApiKey, label: 'API Key', description: 'Send static API key via header/query/cookie.' },
  { value: CredentialApiAuthTypeschema.Basic, label: 'Basic Auth', description: 'Username + password for HTTP Basic.' },
  { value: CredentialApiAuthTypeschema.Bearer, label: 'Bearer Token', description: 'JWT or opaque bearer token.' },
  { value: CredentialApiAuthTypeschema.Mtls, label: 'mTLS', description: 'Mutual TLS with PEM or PKCS#12 materials.' }
];

const apiKeyPlacements: Array<{ value: ApiKeyPlacement; label: string }> = [
  { value: CredentialDetailsApiRestAuthApiKeyInEnum.Header, label: 'Header' },
  { value: CredentialDetailsApiRestAuthApiKeyInEnum.Query, label: 'Query' },
  { value: CredentialDetailsApiRestAuthApiKeyInEnum.Cookie, label: 'Cookie' }
];

const CredentialWizard = ({ orgId, open, mode, initialCredential, onClose, onCompleted }: CredentialWizardProps) => {
  const credentialsApi = useCredentialsApi();
  const secretsApi = useSecretsApi();
  const credentialStoresApi = useCredentialStoresApi();

  const credentialStoresFetcher = useMemo(() => {
    if (!orgId || !open) {
      return null;
    }
    return () => credentialStoresApi.orgsOrgIdCredentialStoresGet({ orgId, limit: 200 });
  }, [credentialStoresApi, open, orgId]);

  const {
    data: credentialStoresList,
    loading: credentialStoresLoading,
    error: credentialStoresError
  } = useApiQuery<CredentialStoresListResponse>(credentialStoresFetcher, [credentialStoresFetcher]);

  const credentialStores: CredentialStore[] = credentialStoresList?.items ?? [];
  const sortedCredentialStores = useMemo(
    () => [...credentialStores].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [credentialStores]
  );

  const [kind, setKind] = useState<CredentialKindValue>(CredentialKindschema.CliSsh);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [labelPairs, setLabelPairs] = useState<LabelPair[]>([]);
  const [labelsError, setLabelsError] = useState<string | null>(null);

  const [cliState, setCliState] = useState<CliFormState>(emptyCliState);
  const [gnmiState, setGnmiState] = useState<GnmiFormState>(emptyGnmiState);
  const [snmpV2cState, setSnmpV2cState] = useState<SnmpV2cFormState>(emptySnmpV2cState);
  const [snmpV3State, setSnmpV3State] = useState<SnmpV3FormState>(emptySnmpV3State);
  const [apiRestState, setApiRestState] = useState<ApiRestFormState>(emptyApiRestState);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setKind(initialCredential?.kind ?? CredentialKindschema.CliSsh);
    setName(initialCredential?.name ?? '');
    setDescription(initialCredential?.description ?? '');
    setLabelPairs(mapLabelsToPairs(initialCredential?.labels));
    setLabelsError(null);
    setCliState(prepopulateCliState(initialCredential));
    setGnmiState(prepopulateGnmiState(initialCredential));
    setSnmpV2cState(prepopulateSnmpV2cState(initialCredential));
    setSnmpV3State(prepopulateSnmpV3State(initialCredential));
    setApiRestState(prepopulateApiRestState(initialCredential));
    setSubmitting(false);
    setSubmitError(null);
  }, [initialCredential]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const validateDetails = useCallback((): string | null => {
    switch (kind) {
      case CredentialKindschema.CliSsh:
        if (!cliState.username.trim()) {
          return 'CLI username is required.';
        }
        if (cliState.authMethod === 'password' && !cliState.execPassword.trim()) {
          return 'CLI password is required.';
        }
        if (cliState.authMethod === 'sshKey' && !cliState.sshPrivateKey.trim()) {
          return 'SSH private key is required.';
        }
        if (cliState.authMethod === 'cyberark' && !cliState.cyberarkStoreId) {
          return 'Select a CyberArk credential store.';
        }
        if (cliState.includeEnablePassword && !cliState.enablePassword.trim()) {
          return 'Enable password is required.';
        }
        return null;
      case CredentialKindschema.Gnmi:
        if (!gnmiState.username.trim()) {
          return 'gNMI username is required.';
        }
        if (!gnmiState.password.trim()) {
          return 'gNMI password is required.';
        }
        if (!Number.isInteger(gnmiState.port) || gnmiState.port < 1 || gnmiState.port > 65535) {
          return 'gNMI port must be between 1 and 65535.';
        }
        if (!containsCertificatePemBlock(gnmiState.deviceServerTrustCaBundle)) {
          return 'Enter a complete PEM Device CA bundle.';
        }
        return null;
      case CredentialKindschema.SnmpV2c:
        if (!snmpV2cState.readCommunity.trim()) {
          return 'SNMP read community is required.';
        }
        if (snmpV2cState.includeWriteCommunity && !snmpV2cState.writeCommunity.trim()) {
          return 'SNMP write community is required.';
        }
        return null;
      case CredentialKindschema.SnmpV3:
        if (!snmpV3State.username.trim()) {
          return 'SNMP v3 username is required.';
        }
        if (snmpV3State.securityLevel !== SnmpV3SecurityLevelschema.NoAuthNoPriv) {
          if (!snmpV3State.authProtocol) {
            return 'Authentication protocol is required.';
          }
          if (!snmpV3State.authPassword.trim()) {
            return 'Authentication password is required.';
          }
        }
        if (snmpV3State.securityLevel === SnmpV3SecurityLevelschema.AuthPriv) {
          if (!snmpV3State.privProtocol) {
            return 'Privacy protocol is required.';
          }
          if (!snmpV3State.privPassword.trim()) {
            return 'Privacy password is required.';
          }
        }
        return null;
      case CredentialKindschema.ApiRest:
        switch (apiRestState.authType) {
          case CredentialApiAuthTypeschema.ApiKey:
            return apiRestState.apiKeyValue.trim() ? null : 'API key value is required.';
          case CredentialApiAuthTypeschema.Basic:
            return apiRestState.basicUsername.trim() && apiRestState.basicPassword.trim()
              ? null
              : 'Username and password are required.';
          case CredentialApiAuthTypeschema.Bearer:
            return apiRestState.bearerToken.trim() ? null : 'Bearer token is required.';
          case CredentialApiAuthTypeschema.Mtls:
            if (apiRestState.mtlsMode === 'pem') {
              return apiRestState.mtlsClientCert.trim() && apiRestState.mtlsClientKey.trim()
                ? null
                : 'Client certificate and key are required.';
            }
            return apiRestState.mtlsPkcs12.trim() ? null : 'PKCS#12 archive is required.';
          default:
            return 'Select an authentication method.';
        }
      default:
        return null;
    }
  }, [apiRestState, cliState, gnmiState, kind, snmpV2cState, snmpV3State]);

  const selectedKindOption = useMemo(() => kindOptions.find((option) => option.value === kind), [kind]);

  const cleanupCreatedSecrets = useCallback(
    async (records: Record<string, CreatedSecret>) => {
      await Promise.all(
        Object.values(records).map(async (entry) => {
          const secretId = entry.secret.id;
          if (!secretId) {
            return;
          }
          try {
            await deleteSecretWithAuth({
              secretsApi,
              orgId,
              secretId,
              etag: entry.etag ?? undefined
            });
          } catch (cleanupError) {
            console.warn('Failed to clean up provisional secret', cleanupError);
          }
        })
      );
    },
    [orgId, secretsApi]
  );

  const handleSecretCreation = useCallback(
    async (definitions: Array<{ key: string; create: SecretCreate }>): Promise<Record<string, CreatedSecret>> => {
      if (definitions.length === 0) {
        return {};
      }
      const entries: Array<[string, CreatedSecret]> = [];
      try {
        for (const definition of definitions) {
          const response = await secretsApi.orgsOrgIdSecretsPostRaw({
            orgId,
            secretCreate: definition.create
          });
          const secret = await response.value();
          const etag = response.raw.headers.get('ETag');
          if (!secret.id) {
            throw new Error('Secret creation response missing identifier.');
          }
          entries.push([definition.key, { secret, etag }]);
        }
        return Object.fromEntries(entries);
      } catch (error) {
        const createdSoFar = Object.fromEntries(entries);
        if (Object.keys(createdSoFar).length > 0) {
          await cleanupCreatedSecrets(createdSoFar);
        }
        throw error;
      }
    },
    [cleanupCreatedSecrets, orgId, secretsApi]
  );

  const buildCliDetails = (
    createdSecrets: Record<string, CreatedSecret>
  ): CredentialDetailsCliSshschema => {
    const details: CliDetailsWithAuth = {
      username: cliState.username.trim(),
      port: Number.isFinite(cliState.port) ? cliState.port : 22
    };
    if (cliState.authMethod === 'password') {
      const entry = createdSecrets['execPassword'];
      if (entry?.secret.id) {
        details.execPasswordSecretId = entry.secret.id;
      }
    } else if (cliState.authMethod === 'sshKey') {
      const keyEntry = createdSecrets['sshPrivateKey'];
      if (keyEntry?.secret.id) {
        details.sshPrivateKeySecretId = keyEntry.secret.id;
      }
      const passphraseEntry = createdSecrets['sshPassphrase'];
      if (passphraseEntry?.secret.id) {
        details.sshPassphraseSecretId = passphraseEntry.secret.id;
      }
    } else {
      if (cliState.cyberarkStoreId) {
        details.cyberarkStoreId = cliState.cyberarkStoreId;
      }
    }
    if (cliState.includeEnablePassword) {
      const enableEntry = createdSecrets['enablePassword'];
      if (enableEntry?.secret.id) {
        details.enablePasswordSecretId = enableEntry.secret.id;
      }
    }
    return details as CredentialDetailsCliSshschema;
  };

  const buildGnmiDetails = (
    createdSecrets: Record<string, CreatedSecret>
  ): CredentialDetailsGnmischema => ({
    username: gnmiState.username.trim(),
    passwordSecretId: createdSecrets['gnmiPassword']?.secret.id ?? '',
    deviceServerTrustCaBundleSecretId: createdSecrets['deviceServerTrustCaBundle']?.secret.id ?? '',
    port: Number.isFinite(gnmiState.port) ? gnmiState.port : 6030
  });

  const renderBasicsSection = () => (
    <section className="section-card">
      <h3 className="section-card__title">Basics</h3>
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Display name</span>
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} placeholder="Example: edge-snmp" />
        </label>
        <label className="form-field">
          <span className="form-field__label">Description</span>
          <input className="input" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Internal notes" />
        </label>
      </div>
      <label className="form-field">
        <span className="form-field__label">Kind</span>
        <select className="select" value={kind} onChange={(event) => setKind(event.target.value as CredentialKindValue)}>
          {kindOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="form-field__hint">{selectedKindOption?.summary}</span>
      </label>
    </section>
  );

  const renderLabelsSection = () => (
    <section className="section-card">
      <h3 className="section-card__title">Labels</h3>
      <LabelPairsEditor pairs={labelPairs} onChange={setLabelPairs} emptyHint={LABELS_EMPTY_HINT} />
    </section>
  );

  const renderKindSection = () => {
    switch (kind) {
      case CredentialKindschema.CliSsh:
        return renderCliDetails();
      case CredentialKindschema.Gnmi:
        return renderGnmiDetails();
      case CredentialKindschema.SnmpV2c:
        return renderSnmpV2cDetails();
      case CredentialKindschema.SnmpV3:
        return renderSnmpV3Details();
      case CredentialKindschema.ApiRest:
        return renderApiRestDetails();
      default:
        return null;
    }
  };

  const renderGnmiDetails = () => (
    <section className="section-card">
      <h3 className="section-card__title">gNMI details</h3>
      <p className="section-card__subtitle">
        The agent verifies the device server certificate using the Device CA. The certificate must be valid for the
        configured management address; IP-addressed devices require a matching IP SAN. Client certificates are not used.
      </p>
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Username</span>
          <input
            className="input"
            value={gnmiState.username}
            onChange={(event) => setGnmiState((prev) => ({ ...prev, username: event.target.value }))}
            autoComplete="username"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Port</span>
          <input
            className="input"
            type="number"
            min={1}
            max={65535}
            value={gnmiState.port}
            onChange={(event) => setGnmiState((prev) => ({ ...prev, port: Number(event.target.value) || 6030 }))}
          />
        </label>
      </div>
      <label className="form-field">
        <span className="form-field__label">Password</span>
        <input
          className="input"
          type="password"
          value={gnmiState.password}
          onChange={(event) => setGnmiState((prev) => ({ ...prev, password: event.target.value }))}
          autoComplete="new-password"
        />
      </label>
      <label className="form-field">
        <span className="form-field__label">Device CA bundle (PEM)</span>
        <input
          className="input"
          type="file"
          accept=".crt,.pem,application/x-pem-file,text/plain"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) {
              return;
            }
            const value = await file.text();
            setGnmiState((prev) => ({ ...prev, deviceServerTrustCaBundle: value }));
          }}
        />
        <span className="form-field__hint">Upload the public CA certificate or PEM bundle used to verify the device gNMI server certificate.</span>
      </label>
      <label className="form-field">
        <span className="form-field__label">Device CA PEM contents</span>
        <textarea
          className="textarea"
          value={gnmiState.deviceServerTrustCaBundle}
          onChange={(event) => setGnmiState((prev) => ({ ...prev, deviceServerTrustCaBundle: event.target.value }))}
          placeholder="-----BEGIN CERTIFICATE-----"
          spellCheck={false}
        />
      </label>
    </section>
  );

  const buildSnmpV2cDetails = (
    createdSecrets: Record<string, CreatedSecret>
  ): CredentialDetailsSnmpV2cschema => {
    const details: CredentialDetailsSnmpV2cschema = {
      port: Number.isFinite(snmpV2cState.port) ? snmpV2cState.port : 161,
      readCommunitySecretId: createdSecrets['readCommunity']?.secret.id ?? ''
    };
    if (snmpV2cState.includeWriteCommunity) {
      const writeEntry = createdSecrets['writeCommunity'];
      if (writeEntry?.secret.id) {
        details.writeCommunitySecretId = writeEntry.secret.id;
      }
    }
    return details;
  };

  const buildSnmpV3Details = (
    createdSecrets: Record<string, CreatedSecret>
  ): CredentialDetailsSnmpV3schema => {
    const details: CredentialDetailsSnmpV3schema = {
      username: snmpV3State.username.trim(),
      port: Number.isFinite(snmpV3State.port) ? snmpV3State.port : 161,
      securityLevel: snmpV3State.securityLevel
    };
    if (snmpV3State.engineId.trim()) {
      details.engineId = snmpV3State.engineId.trim();
    }
    if (snmpV3State.contextName.trim()) {
      details.contextName = snmpV3State.contextName.trim();
    }
    if (snmpV3State.securityLevel !== SnmpV3SecurityLevelschema.NoAuthNoPriv) {
      details.authProtocol = snmpV3State.authProtocol || SnmpV3AuthProtocolschema.Sha;
      const authEntry = createdSecrets['authPassword'];
      if (authEntry?.secret.id) {
        details.authPasswordSecretId = authEntry.secret.id;
      }
    }
    if (snmpV3State.securityLevel === SnmpV3SecurityLevelschema.AuthPriv) {
      details.privProtocol = snmpV3State.privProtocol || SnmpV3PrivProtocolschema.Aes128;
      const privEntry = createdSecrets['privPassword'];
      if (privEntry?.secret.id) {
        details.privPasswordSecretId = privEntry.secret.id;
      }
    }
    return details;
  };

  const buildApiRestDetails = (
    createdSecrets: Record<string, CreatedSecret>
  ): CredentialDetailsApiRestAuth => {
    const authType = apiRestState.authType;
    const result: CredentialDetailsApiRestAuth = {
      type: authType
    };
    if (authType === CredentialApiAuthTypeschema.ApiKey) {
      const entry = createdSecrets['apiKey'];
      const payload: CredentialDetailsApiRestAuthApiKey = {
        _in: apiRestState.apiKeyPlacement,
        name: apiRestState.apiKeyName.trim() || 'X-Api-Key',
        apiKeySecretId: entry?.secret.id ?? ''
      };
      result.apiKey = payload;
    } else if (authType === CredentialApiAuthTypeschema.Basic) {
      const entry = createdSecrets['basicPassword'];
      const payload: CredentialDetailsApiRestAuthBasic = {
        username: apiRestState.basicUsername.trim(),
        passwordSecretId: entry?.secret.id ?? ''
      };
      result.basic = payload;
    } else if (authType === CredentialApiAuthTypeschema.Bearer) {
      const entry = createdSecrets['bearerToken'];
      const payload: CredentialDetailsApiRestAuthBearer = {
        tokenSecretId: entry?.secret.id ?? ''
      };
      result.bearer = payload;
    } else if (authType === CredentialApiAuthTypeschema.Mtls) {
      if (apiRestState.mtlsMode === 'pem') {
        const clientCert = createdSecrets['mtlsClientCert'];
        const clientKey = createdSecrets['mtlsClientKey'];
        const caBundle = createdSecrets['mtlsCaBundle'];
        result.mtls = {
          clientCertSecretId: clientCert?.secret.id ?? '',
          clientKeySecretId: clientKey?.secret.id ?? '',
          caBundleSecretId: caBundle?.secret.id ?? undefined
        };
      } else {
        const pkcs12 = createdSecrets['mtlsPkcs12'];
        const passphrase = createdSecrets['mtlsPkcs12Passphrase'];
        result.mtls = {
          pkcs12SecretId: pkcs12?.secret.id ?? '',
          pkcs12PassphraseSecretId: passphrase?.secret.id ?? undefined
        };
      }
    }
    return result;
  };

  const buildSecretDefinitions = (): Array<{ key: string; create: SecretCreate }> => {
    const entries: Array<{ key: string; create: SecretCreate }> = [];
    if (kind === CredentialKindschema.Gnmi) {
      entries.push({
        key: 'gnmiPassword',
        create: { type: SecretTypeschema.Password, value: gnmiState.password }
      });
      entries.push({
        key: 'deviceServerTrustCaBundle',
        create: { type: SecretTypeschema.TlsCaBundlePem, value: gnmiState.deviceServerTrustCaBundle }
      });
    } else if (kind === CredentialKindschema.CliSsh) {
      if (cliState.authMethod === 'password') {
        entries.push({
          key: 'execPassword',
          create: {
            type: SecretTypeschema.Password,
            value: cliState.execPassword
          }
        });
      } else if (cliState.authMethod === 'sshKey') {
        entries.push({
          key: 'sshPrivateKey',
          create: {
            type: SecretTypeschema.SshPrivateKey,
            value: cliState.sshPrivateKey
          }
        });
        if (cliState.sshPassphrase.trim()) {
          entries.push({
            key: 'sshPassphrase',
            create: {
              type: SecretTypeschema.SshPassphrase,
              value: cliState.sshPassphrase
            }
          });
        }
      }
      if (cliState.includeEnablePassword && cliState.enablePassword.trim()) {
        entries.push({
          key: 'enablePassword',
          create: {
            type: SecretTypeschema.EnablePassword,
            value: cliState.enablePassword
          }
        });
      }
    } else if (kind === CredentialKindschema.SnmpV2c) {
      entries.push({
        key: 'readCommunity',
        create: {
          type: SecretTypeschema.SnmpCommunity,
          value: snmpV2cState.readCommunity
        }
      });
      if (snmpV2cState.includeWriteCommunity && snmpV2cState.writeCommunity.trim()) {
        entries.push({
          key: 'writeCommunity',
          create: {
            type: SecretTypeschema.SnmpCommunity,
            value: snmpV2cState.writeCommunity
          }
        });
      }
    } else if (kind === CredentialKindschema.SnmpV3) {
      if (snmpV3State.securityLevel !== SnmpV3SecurityLevelschema.NoAuthNoPriv) {
        entries.push({
          key: 'authPassword',
          create: {
            type: SecretTypeschema.SnmpAuthPassword,
            value: snmpV3State.authPassword
          }
        });
      }
      if (snmpV3State.securityLevel === SnmpV3SecurityLevelschema.AuthPriv) {
        entries.push({
          key: 'privPassword',
          create: {
            type: SecretTypeschema.SnmpPrivPassword,
            value: snmpV3State.privPassword
          }
        });
      }
    } else if (kind === CredentialKindschema.ApiRest) {
      if (apiRestState.authType === CredentialApiAuthTypeschema.ApiKey) {
        entries.push({
          key: 'apiKey',
          create: {
            type: SecretTypeschema.ApiKey,
            value: apiRestState.apiKeyValue
          }
        });
      } else if (apiRestState.authType === CredentialApiAuthTypeschema.Basic) {
        entries.push({
          key: 'basicPassword',
          create: {
            type: SecretTypeschema.Password,
            value: apiRestState.basicPassword
          }
        });
      } else if (apiRestState.authType === CredentialApiAuthTypeschema.Bearer) {
        entries.push({
          key: 'bearerToken',
          create: {
            type: SecretTypeschema.BearerToken,
            value: apiRestState.bearerToken
          }
        });
      } else if (apiRestState.authType === CredentialApiAuthTypeschema.Mtls) {
        if (apiRestState.mtlsMode === 'pem') {
          entries.push({
            key: 'mtlsClientCert',
            create: { type: SecretTypeschema.TlsClientCertPem, value: apiRestState.mtlsClientCert }
          });
          entries.push({
            key: 'mtlsClientKey',
            create: { type: SecretTypeschema.TlsClientKeyPem, value: apiRestState.mtlsClientKey }
          });
          if (apiRestState.mtlsCaBundle.trim()) {
            entries.push({
              key: 'mtlsCaBundle',
              create: { type: SecretTypeschema.TlsCaBundlePem, value: apiRestState.mtlsCaBundle }
            });
          }
        } else {
          entries.push({
            key: 'mtlsPkcs12',
            create: { type: SecretTypeschema.CertificatePkcs12, value: apiRestState.mtlsPkcs12 }
          });
          if (apiRestState.mtlsPkcs12Passphrase.trim()) {
            entries.push({
              key: 'mtlsPkcs12Passphrase',
              create: { type: SecretTypeschema.Password, value: apiRestState.mtlsPkcs12Passphrase }
            });
          }
        }
      }
    }
    return entries;
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setSubmitError(null);
    let createdSecrets: Record<string, CreatedSecret> = {};
    try {
      const labelsResult = labelPairsToRecord(labelPairs);
      if (labelsResult.error) {
        setLabelsError(labelsResult.error);
        setSubmitting(false);
        return;
      }
      setLabelsError(null);
      const detailError = validateDetails();
      if (detailError) {
        setSubmitError(detailError);
        setSubmitting(false);
        return;
      }
      const secretDefinitions = buildSecretDefinitions();
      createdSecrets = await handleSecretCreation(secretDefinitions);
      const payloadBase = {
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        labels: Object.keys(labelsResult.labels).length > 0 ? labelsResult.labels : undefined
      };
      const base = buildCredentialCreate(kind, payloadBase, createdSecrets);

      const response = await credentialsApi.orgsOrgIdCredentialsPostRaw({
        orgId,
        credentialCreate: base
      });
      const credential = await response.value();
      const etag = response.raw.headers.get('ETag');
      onCompleted(credential, etag);
    } catch (error) {
      if (Object.keys(createdSecrets).length > 0) {
        await cleanupCreatedSecrets(createdSecrets);
      }
      console.error('Failed to create credential', error);
      setSubmitError(await resolveErrorMessage(error, 'Failed to create credential.'));
    } finally {
      setSubmitting(false);
    }
  };

  const buildCredentialCreate = (
    selectedKind: CredentialKindValue,
    base: Pick<CredentialCreate, 'name' | 'description' | 'labels'>,
    createdSecrets: Record<string, CreatedSecret>
  ): CredentialCreate => {
    switch (selectedKind) {
      case CredentialKindschema.CliSsh:
        return {
          ...base,
          kind: CredentialKindschema.CliSsh,
          details: buildCliDetails(createdSecrets)
        } as CredentialCreate;
      case CredentialKindschema.Gnmi:
        return {
          ...base,
          kind: CredentialKindschema.Gnmi,
          details: buildGnmiDetails(createdSecrets)
        } as CredentialCreate;
      case CredentialKindschema.SnmpV2c:
        return {
          ...base,
          kind: CredentialKindschema.SnmpV2c,
          details: buildSnmpV2cDetails(createdSecrets)
        } as CredentialCreate;
      case CredentialKindschema.SnmpV3:
        return {
          ...base,
          kind: CredentialKindschema.SnmpV3,
          details: buildSnmpV3Details(createdSecrets)
        } as CredentialCreate;
      case CredentialKindschema.ApiRest:
        return {
          ...base,
          kind: CredentialKindschema.ApiRest,
          details: {
            auth: buildApiRestDetails(createdSecrets)
          }
        } as CredentialCreate;
      default:
        return {
          ...base,
          kind: CredentialKindschema.CliSsh,
          details: buildCliDetails(createdSecrets)
        } as CredentialCreate;
    }
  };

  const renderCliDetails = () => (
    <div className="section-card">
      <h3 className="section-card__title">CLI (SSH) details</h3>
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Username</span>
          <input className="input" value={cliState.username} onChange={(event) => setCliState((prev) => ({ ...prev, username: event.target.value }))} />
        </label>
        <label className="form-field">
          <span className="form-field__label">Port</span>
          <input
            className="input"
            type="number"
            min={1}
            max={65535}
            value={cliState.port}
            onChange={(event) => setCliState((prev) => ({ ...prev, port: Number(event.target.value) || 22 }))}
          />
        </label>
      </div>
      <div className="form-grid">
        <div className="section-card">
          <h4 className="section-card__title">Primary authentication</h4>
          <label className="radio-row">
            <input
              className="radio"
              type="radio"
              name="cli-auth"
              value="password"
              checked={cliState.authMethod === 'password'}
              onChange={() => setCliState((prev) => ({ ...prev, authMethod: 'password' }))}
            />
            Password
          </label>
          {cliState.authMethod === 'password' && (
            <label className="form-field">
              <span className="form-field__label">Password</span>
              <input
                className="input"
                type="password"
                value={cliState.execPassword}
                onChange={(event) => setCliState((prev) => ({ ...prev, execPassword: event.target.value }))}
              />
              <span className="form-field__hint">You’ll only see this value once.</span>
            </label>
          )}
          <label className="radio-row">
            <input
              className="radio"
              type="radio"
              name="cli-auth"
              value="ssh"
              checked={cliState.authMethod === 'sshKey'}
              onChange={() => setCliState((prev) => ({ ...prev, authMethod: 'sshKey' }))}
            />
            SSH private key
          </label>
          {cliState.authMethod === 'sshKey' && (
            <Fragment>
              <label className="form-field">
                <span className="form-field__label">Private key (PEM)</span>
                <textarea
                  className="textarea"
                  value={cliState.sshPrivateKey}
                  onChange={(event) => setCliState((prev) => ({ ...prev, sshPrivateKey: event.target.value }))}
                />
                <span className="form-field__hint">Paste the full PEM block. Stored securely server-side.</span>
              </label>
              <label className="form-field">
                <span className="form-field__label">Passphrase (optional)</span>
                <input
                  className="input"
                  type="password"
                  value={cliState.sshPassphrase}
                  onChange={(event) => setCliState((prev) => ({ ...prev, sshPassphrase: event.target.value }))}
                />
              </label>
            </Fragment>
          )}
          <label className="radio-row">
            <input
              className="radio"
              type="radio"
              name="cli-auth"
              value="cyberark"
              checked={cliState.authMethod === 'cyberark'}
              onChange={() => setCliState((prev) => ({ ...prev, authMethod: 'cyberark' }))}
            />
            Credential Store
          </label>
          {cliState.authMethod === 'cyberark' && (
            <div className="form-grid">
              <label className="form-field">
                <span className="form-field__label">Credential store</span>
                <select
                  className="select"
                  value={cliState.cyberarkStoreId}
                  onChange={(event) => setCliState((prev) => ({ ...prev, cyberarkStoreId: event.target.value }))}
                >
                  <option value="">Select a CyberArk store</option>
                  {sortedCredentialStores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
                {credentialStoresLoading && <span className="form-field__hint">Loading credential stores…</span>}
                {!credentialStoresLoading && sortedCredentialStores.length === 0 && (
                  <span className="form-field__hint">No credential stores yet. Add one in the Credential Store tab.</span>
                )}
                {credentialStoresError && (
                  <span className="form-field__hint">Failed to load credential stores: {credentialStoresError.message}</span>
                )}
              </label>
              <p className="form-field__hint">
                Passwords are fetched from CyberArk CCP at runtime using the selected credential store.
              </p>
            </div>
          )}
        </div>
        <label className="checkbox-row">
          <input
            className="checkbox"
            type="checkbox"
            checked={cliState.includeEnablePassword}
            onChange={(event) => setCliState((prev) => ({ ...prev, includeEnablePassword: event.target.checked }))}
          />
          Provide enable password
        </label>
        {cliState.includeEnablePassword && (
          <label className="form-field">
            <span className="form-field__label">Enable password</span>
            <input
              type="password"
              className="input"
              value={cliState.enablePassword}
              onChange={(event) => setCliState((prev) => ({ ...prev, enablePassword: event.target.value }))}
            />
          </label>
        )}
      </div>
    </div>
  );

  const renderSnmpV2cDetails = () => (
    <div className="section-card">
      <h3 className="section-card__title">SNMP v2c details</h3>
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Port</span>
          <input
            className="input"
            type="number"
            min={1}
            max={65535}
            value={snmpV2cState.port}
            onChange={(event) => setSnmpV2cState((prev) => ({ ...prev, port: Number(event.target.value) || 161 }))}
          />
        </label>
      </div>
      <label className="form-field">
        <span className="form-field__label">Read community</span>
        <input
          className="input"
          type="password"
          value={snmpV2cState.readCommunity}
          onChange={(event) => setSnmpV2cState((prev) => ({ ...prev, readCommunity: event.target.value }))}
        />
      </label>
      <label className="checkbox-row">
        <input
          className="checkbox"
          type="checkbox"
          checked={snmpV2cState.includeWriteCommunity}
          onChange={(event) => setSnmpV2cState((prev) => ({ ...prev, includeWriteCommunity: event.target.checked }))}
        />
        Provide write community
      </label>
      {snmpV2cState.includeWriteCommunity && (
        <label className="form-field">
          <span className="form-field__label">Write community</span>
          <input
            className="input"
            type="password"
            value={snmpV2cState.writeCommunity}
            onChange={(event) => setSnmpV2cState((prev) => ({ ...prev, writeCommunity: event.target.value }))}
          />
        </label>
      )}
    </div>
  );

  const renderSnmpV3Details = () => (
    <div className="section-card">
      <h3 className="section-card__title">SNMP v3 details</h3>
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Username</span>
          <input
            className="input"
            value={snmpV3State.username}
            onChange={(event) => setSnmpV3State((prev) => ({ ...prev, username: event.target.value }))}
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Port</span>
          <input
            className="input"
            type="number"
            min={1}
            max={65535}
            value={snmpV3State.port}
            onChange={(event) => setSnmpV3State((prev) => ({ ...prev, port: Number(event.target.value) || 161 }))}
          />
        </label>
      </div>
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Engine ID (optional)</span>
          <input
            className="input"
            value={snmpV3State.engineId}
            onChange={(event) => setSnmpV3State((prev) => ({ ...prev, engineId: event.target.value }))}
            placeholder="0x010203..."
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Context name (optional)</span>
          <input
            className="input"
            value={snmpV3State.contextName}
            onChange={(event) => setSnmpV3State((prev) => ({ ...prev, contextName: event.target.value }))}
          />
        </label>
      </div>
      <label className="form-field">
        <span className="form-field__label">Security level</span>
        <select
          className="select"
          value={snmpV3State.securityLevel}
          onChange={(event) =>
        setSnmpV3State((prev) => ({
          ...prev,
          securityLevel: event.target.value as SnmpSecurityLevel
        }))
          }
        >
          {securityLevels.map((level) => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>
      </label>
      {snmpV3State.securityLevel !== SnmpV3SecurityLevelschema.NoAuthNoPriv && (
        <div className="form-grid form-grid--two">
          <label className="form-field">
            <span className="form-field__label">Auth protocol</span>
            <select
              className="select"
              value={snmpV3State.authProtocol}
              onChange={(event) =>
                setSnmpV3State((prev) => ({
                  ...prev,
                  authProtocol: event.target.value as SnmpAuthProtocol | ''
                }))
              }
            >
              <option value="">Select protocol</option>
              {authProtocols.map((protocol) => (
                <option key={protocol.value} value={protocol.value}>
                  {protocol.label}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Auth password</span>
            <input
              className="input"
              type="password"
              value={snmpV3State.authPassword}
              onChange={(event) => setSnmpV3State((prev) => ({ ...prev, authPassword: event.target.value }))}
            />
          </label>
        </div>
      )}
      {snmpV3State.securityLevel === SnmpV3SecurityLevelschema.AuthPriv && (
        <div className="form-grid form-grid--two">
          <label className="form-field">
            <span className="form-field__label">Privacy protocol</span>
            <select
              className="select"
              value={snmpV3State.privProtocol}
              onChange={(event) =>
                setSnmpV3State((prev) => ({
                  ...prev,
                  privProtocol: event.target.value as SnmpPrivProtocol | ''
                }))
              }
            >
              <option value="">Select protocol</option>
              {privProtocols.map((protocol) => (
                <option key={protocol.value} value={protocol.value}>
                  {protocol.label}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span className="form-field__label">Privacy password</span>
            <input
              className="input"
              type="password"
              value={snmpV3State.privPassword}
              onChange={(event) => setSnmpV3State((prev) => ({ ...prev, privPassword: event.target.value }))}
            />
          </label>
        </div>
      )}
    </div>
  );

  const renderApiRestDetails = () => (
    <div className="section-card">
      <h3 className="section-card__title">API (REST) details</h3>
      <div className="form-grid">
        <div className="form-grid form-grid--two">
          {apiAuthOptions.map((option) => (
            <label key={option.value} className="section-card">
              <span className="radio-row">
                <input
                  type="radio"
                  className="radio"
                  name="api-auth-type"
                  value={option.value}
                  checked={apiRestState.authType === option.value}
                  onChange={() => setApiRestState((prev) => ({ ...prev, authType: option.value }))}
                />
                <span>
                  <strong>{option.label}</strong>
                  <br />
                  <span className="helper-text">{option.description}</span>
                </span>
              </span>
            </label>
          ))}
        </div>
        {apiRestState.authType === CredentialApiAuthTypeschema.ApiKey && (
          <div className="form-grid form-grid--two">
            <label className="form-field">
              <span className="form-field__label">Placement</span>
              <select
                className="select"
                value={apiRestState.apiKeyPlacement}
                onChange={(event) =>
                  setApiRestState((prev) => ({
                    ...prev,
                    apiKeyPlacement: event.target.value as ApiKeyPlacement
                  }))
                }
              >
                {apiKeyPlacements.map((placement) => (
                  <option key={placement.value} value={placement.value}>
                    {placement.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span className="form-field__label">Parameter name</span>
              <input
                className="input"
                value={apiRestState.apiKeyName}
                onChange={(event) => setApiRestState((prev) => ({ ...prev, apiKeyName: event.target.value }))}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">API key</span>
              <input
                className="input"
                type="password"
                value={apiRestState.apiKeyValue}
                onChange={(event) => setApiRestState((prev) => ({ ...prev, apiKeyValue: event.target.value }))}
              />
            </label>
          </div>
        )}
        {apiRestState.authType === CredentialApiAuthTypeschema.Basic && (
          <div className="form-grid form-grid--two">
            <label className="form-field">
              <span className="form-field__label">Username</span>
              <input
                className="input"
                value={apiRestState.basicUsername}
                onChange={(event) => setApiRestState((prev) => ({ ...prev, basicUsername: event.target.value }))}
              />
            </label>
            <label className="form-field">
              <span className="form-field__label">Password</span>
              <input
                className="input"
                type="password"
                value={apiRestState.basicPassword}
                onChange={(event) => setApiRestState((prev) => ({ ...prev, basicPassword: event.target.value }))}
              />
            </label>
          </div>
        )}
        {apiRestState.authType === CredentialApiAuthTypeschema.Bearer && (
          <label className="form-field">
            <span className="form-field__label">Bearer token</span>
            <textarea
              className="textarea"
              value={apiRestState.bearerToken}
              onChange={(event) => setApiRestState((prev) => ({ ...prev, bearerToken: event.target.value }))}
            />
          </label>
        )}
        {apiRestState.authType === CredentialApiAuthTypeschema.Mtls && (
          <div className="form-grid">
            <div className="radio-row">
              <label className="radio-row">
                <input
                  type="radio"
                  className="radio"
                  name="mtls-mode"
                  value="pem"
                  checked={apiRestState.mtlsMode === 'pem'}
                  onChange={() => setApiRestState((prev) => ({ ...prev, mtlsMode: 'pem' }))}
                />
                PEM pair
              </label>
              <label className="radio-row">
                <input
                  type="radio"
                  className="radio"
                  name="mtls-mode"
                  value="pkcs12"
                  checked={apiRestState.mtlsMode === 'pkcs12'}
                  onChange={() => setApiRestState((prev) => ({ ...prev, mtlsMode: 'pkcs12' }))}
                />
                PKCS#12 bundle
              </label>
            </div>
            {apiRestState.mtlsMode === 'pem' ? (
              <Fragment>
                <label className="form-field">
                  <span className="form-field__label">Client certificate (PEM)</span>
                  <textarea
                    className="textarea"
                    value={apiRestState.mtlsClientCert}
                    onChange={(event) => setApiRestState((prev) => ({ ...prev, mtlsClientCert: event.target.value }))}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">Client key (PEM)</span>
                  <textarea
                    className="textarea"
                    value={apiRestState.mtlsClientKey}
                    onChange={(event) => setApiRestState((prev) => ({ ...prev, mtlsClientKey: event.target.value }))}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">CA bundle (optional)</span>
                  <textarea
                    className="textarea"
                    value={apiRestState.mtlsCaBundle}
                    onChange={(event) => setApiRestState((prev) => ({ ...prev, mtlsCaBundle: event.target.value }))}
                  />
                </label>
              </Fragment>
            ) : (
              <Fragment>
                <label className="form-field">
                  <span className="form-field__label">PKCS#12 bundle</span>
                  <textarea
                    className="textarea"
                    value={apiRestState.mtlsPkcs12}
                    onChange={(event) => setApiRestState((prev) => ({ ...prev, mtlsPkcs12: event.target.value }))}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">Passphrase (optional)</span>
                  <input
                    className="input"
                    type="password"
                    value={apiRestState.mtlsPkcs12Passphrase}
                    onChange={(event) => setApiRestState((prev) => ({ ...prev, mtlsPkcs12Passphrase: event.target.value }))}
                  />
                </label>
              </Fragment>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const drawerActions = (
    <div className="drawer-icon-actions">
      <button
        type="button"
        className="drawer-icon-button"
        aria-label="Save credential"
        title="Save credential"
        onClick={handleCreate}
        disabled={submitting}
      >
        <SaveIcon size={18} />
      </button>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === 'duplicate' ? 'Duplicate credential' : 'New credential'}
      subtitle="Secrets are captured once and stored securely. Rotate them later from the detail view."
      width="wide"
      actions={drawerActions}
    >
      {submitError && <div className="form-error">{submitError}</div>}
      {labelsError && <div className="form-error">{labelsError}</div>}
      <div className="form-grid">
        {renderBasicsSection()}
        {renderLabelsSection()}
        {renderKindSection()}
      </div>
    </Drawer>
  );
};

const prepopulateCliState = (credential: Credential | null | undefined): CliFormState => {
  if (!credential || credential.kind !== CredentialKindschema.CliSsh) {
    return emptyCliState();
  }
  const details = credential.details as Partial<CliDetailsWithAuth>;
  const authValue = (details as CliDetailsWithAuth).auth;
  const authMethod: CliAuthMethod =
    authValue === 'cyberark' || details.cyberarkStoreId
      ? 'cyberark'
      : details.sshPrivateKeySecretId
        ? 'sshKey'
        : 'password';
  return {
    username: details.username ?? '',
    port: details.port ?? 22,
    authMethod,
    execPassword: '',
    enablePassword: '',
    includeEnablePassword: Boolean(details.enablePasswordSecretId),
    sshPrivateKey: '',
    sshPassphrase: '',
    cyberarkStoreId: details.cyberarkStoreId ?? ''
  };
};

const prepopulateGnmiState = (credential: Credential | null | undefined): GnmiFormState => {
  if (credential?.kind !== CredentialKindschema.Gnmi) {
    return emptyGnmiState();
  }
  const details = credential.details as CredentialDetailsGnmischema;
  return {
    ...emptyGnmiState(),
    username: details.username ?? '',
    port: details.port ?? 6030
  };
};

const prepopulateSnmpV2cState = (credential: Credential | null | undefined): SnmpV2cFormState => {
  if (!credential || credential.kind !== CredentialKindschema.SnmpV2c) {
    return emptySnmpV2cState();
  }
  const details = credential.details as Partial<CredentialDetailsSnmpV2cschema>;
  return {
    port: details.port ?? 161,
    readCommunity: '',
    writeCommunity: '',
    includeWriteCommunity: Boolean(details.writeCommunitySecretId)
  };
};

const prepopulateSnmpV3State = (credential: Credential | null | undefined): SnmpV3FormState => {
  if (!credential || credential.kind !== CredentialKindschema.SnmpV3) {
    return emptySnmpV3State();
  }
  const details = credential.details as Partial<CredentialDetailsSnmpV3schema>;
  return {
    username: details.username ?? '',
    port: details.port ?? 161,
    engineId: details.engineId ?? '',
    contextName: details.contextName ?? '',
    securityLevel: details.securityLevel ?? SnmpV3SecurityLevelschema.NoAuthNoPriv,
    authProtocol: details.authProtocol ?? '',
    authPassword: '',
    privProtocol: details.privProtocol ?? '',
    privPassword: ''
  };
};

const prepopulateApiRestState = (credential: Credential | null | undefined): ApiRestFormState => {
  if (!credential || credential.kind !== CredentialKindschema.ApiRest) {
    return emptyApiRestState();
  }
  const details = credential.details as { auth?: CredentialDetailsApiRestAuth };
  const auth = details.auth;
  if (!auth) {
    return emptyApiRestState();
  }
  const base = emptyApiRestState();
  base.authType = auth.type ?? CredentialApiAuthTypeschema.ApiKey;
  if (auth.type === CredentialApiAuthTypeschema.ApiKey && auth.apiKey) {
    base.apiKeyPlacement = auth.apiKey._in ?? CredentialDetailsApiRestAuthApiKeyInEnum.Header;
    base.apiKeyName = auth.apiKey.name ?? 'X-Api-Key';
  }
  if (auth.type === CredentialApiAuthTypeschema.Basic && auth.basic) {
    base.basicUsername = auth.basic.username ?? '';
  }
  if (auth.type === CredentialApiAuthTypeschema.Mtls && auth.mtls) {
    if ('clientCertSecretId' in auth.mtls) {
      base.mtlsMode = 'pem';
    } else {
      base.mtlsMode = 'pkcs12';
    }
  }
  return base;
};

export default CredentialWizard;
